/**
 * Saisie d'un nombre à décimales libres.
 *
 * `<input type="number">` ne sait pas faire ça : le séparateur décimal accepté dépend de la locale du
 * navigateur, `step` impose un pas (et invalide tout ce qui tombe entre deux crans), et ce qui n'est pas
 * un « valid floating-point number » est jeté SANS rien dire — `input.value` renvoie alors `''`, donc un
 * prix collé depuis une facture peut devenir vide, ou pire : `1.234,56` est relu `1.23456`.
 *
 * On lit donc le texte nous-mêmes, avec une règle unique et explicite : **le dernier `,` ou `.` est le
 * séparateur décimal**, tout ce qui le précède est un séparateur de milliers. Le champ réaffiche ensuite
 * le nombre retenu, pour que la lecture faite de la saisie soit visible.
 */

/** Ce qu'on laisse taper : chiffres, séparateurs, espaces et symbole euro — une lettre ne s'affiche pas. */
const DECIMAL_DRAFT_REGEX = /^-?[\d.,\s\u20AC]*$/;

/** Espaces (`\s` couvre les insécables) et euro : du décor collé au montant, jamais de la valeur. */
const DECORATION_REGEX = /[\s\u20AC]/g;

export function isDecimalDraft(text: string): boolean {
    return DECIMAL_DRAFT_REGEX.test(text);
}

/** Le texte saisi → le nombre qu'il désigne. `null` dès que ça ne désigne aucun nombre (y compris « 1, »). */
export function parseDecimal(text: string): number | null {
    const cleaned = text.replace(DECORATION_REGEX, '');
    if (!cleaned || cleaned === '-') {
        return null;
    }

    const decimalSeparatorIndex = Math.max(cleaned.lastIndexOf(','), cleaned.lastIndexOf('.'));
    const normalized =
        decimalSeparatorIndex === -1
            ? cleaned
            : `${cleaned.slice(0, decimalSeparatorIndex).replace(/[.,]/g, '')}.${cleaned.slice(decimalSeparatorIndex + 1)}`;

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
}

/** Le nombre → le texte qu'on affiche quand le champ n'a pas le focus. Aucune troncature : c'est une saisie. */
export function formatDecimal(value: number | null | undefined): string {
    return value === null || value === undefined ? '' : String(value).replace('.', ',');
}
